import { useState, useEffect } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Facebook, Instagram, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { mediaUrl } from "@/lib/api";

const NAV = [
  { to: "/jeu", label: "Le Jeu" },
  { to: "/ou-trouver-le-jeu", label: "Où trouver" },
  { to: "/application", label: "Application" },
  { to: "/actualites", label: "Actualités" },
  { to: "/avis", label: "Avis" },
  { to: "/entreprises", label: "Entreprises" },
];

function Logo({ light = false }) {
  const { settings } = useSite();
  const logoUrl = light ? settings?.logo_url_light : settings?.logo_url;
  return (
    <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
      {logoUrl ? (
        <img src={mediaUrl(logoUrl)} alt="Douzième Homme — Le Jeu"
          className={`${light ? "h-14" : "h-11 sm:h-14"} w-auto group-hover:opacity-80 transition-opacity`} />
      ) : (
        <span className={`font-display font-extrabold text-lg sm:text-xl tracking-tight ${light ? "text-cream" : "text-onyx"} group-hover:text-rouge transition-colors`}>
          DOUZIÈME HOMME
        </span>
      )}
      <span className={`font-mono text-[10px] tracking-[0.3em] uppercase font-bold leading-tight ${light ? "text-rouge-light" : "text-rouge"}`}>
        Le Jeu<br />Édition Nice
      </span>
    </Link>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const { settings } = useSite();
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      {settings?.announcement_active === "true" && settings?.announcement_text && (
        <div data-testid="announcement-bar" className="bg-onyx text-cream text-center text-xs sm:text-sm py-2 px-4 font-medium tracking-wide">
          {settings.announcement_text}
        </div>
      )}

      <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-line">
        <div className="container-x flex items-center justify-between h-16 sm:h-20">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} data-testid={`nav-${item.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide transition-colors hover:text-rouge ${isActive ? "text-rouge" : "text-onyx/80"}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/acheter" data-testid="header-cta-acheter"
              className="hidden sm:inline-flex bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
              Acheter le jeu
            </Link>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" aria-label="Ouvrir le menu"
                  className="lg:hidden p-2 text-onyx hover:text-rouge transition-colors">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-cream border-line w-[85vw] sm:w-96 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-line"><Logo /></div>
                  <nav className="flex flex-col p-6 gap-1" data-testid="mobile-nav">
                    {[{ to: "/", label: "Accueil" }, ...NAV, { to: "/faq", label: "FAQ" }, { to: "/contact", label: "Contact" }].map((item) => (
                      <Link key={item.to} to={item.to} data-testid={`mobile-nav-${item.to === "/" ? "accueil" : item.to.slice(1)}`}
                        className="py-3 px-2 text-lg font-display font-bold text-onyx hover:text-rouge border-b border-line/60 transition-colors">
                        {item.label}
                      </Link>
                    ))}
                  </nav>
                  <div className="mt-auto p-6">
                    <Link to="/acheter" data-testid="mobile-cta-acheter"
                      className="block text-center bg-rouge text-white px-5 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
                      Acheter le jeu
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-onyx text-cream/80 mt-0" data-testid="site-footer">
        <div className="container-x py-14 sm:py-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="mb-5"><Logo light /></div>
            <p className="text-sm leading-relaxed text-cream/60">
              Le jeu de société des supporters niçois. Conçu à Nice, fabriqué en France.
            </p>
            <div className="flex gap-3 mt-6">
              <a href={settings?.social_facebook || "#"} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook"
                className="w-10 h-10 border border-cream/20 flex items-center justify-center hover:bg-rouge hover:border-rouge transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={settings?.social_instagram || "#"} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram"
                className="w-10 h-10 border border-cream/20 flex items-center justify-center hover:bg-rouge hover:border-rouge transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={settings?.social_youtube || "#"} target="_blank" rel="noopener noreferrer" data-testid="social-youtube" aria-label="YouTube"
                className="w-10 h-10 border border-cream/20 flex items-center justify-center hover:bg-rouge hover:border-rouge transition-colors">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div>
            <h3 className="font-display font-bold text-cream uppercase tracking-wide text-sm mb-5">Le jeu</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/jeu" data-testid="footer-link-jeu" className="hover:text-rouge-light transition-colors">Découvrir le jeu</Link></li>
              <li><Link to="/acheter" data-testid="footer-link-acheter" className="hover:text-rouge-light transition-colors">Acheter</Link></li>
              <li><Link to="/application" data-testid="footer-link-application" className="hover:text-rouge-light transition-colors">Application compagnon</Link></li>
              <li><Link to="/ou-trouver-le-jeu" data-testid="footer-link-ou-trouver" className="hover:text-rouge-light transition-colors">Où trouver le jeu</Link></li>
              <li><Link to="/entreprises" data-testid="footer-link-entreprises" className="hover:text-rouge-light transition-colors">Offre entreprises</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-display font-bold text-cream uppercase tracking-wide text-sm mb-5">Aide & infos</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/actualites" data-testid="footer-link-actualites" className="hover:text-rouge-light transition-colors">Actualités</Link></li>
              <li><Link to="/avis" data-testid="footer-link-avis" className="hover:text-rouge-light transition-colors">Avis</Link></li>
              <li><Link to="/faq" data-testid="footer-link-faq" className="hover:text-rouge-light transition-colors">FAQ</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-rouge-light transition-colors">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-display font-bold text-cream uppercase tracking-wide text-sm mb-5">Contact</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-rouge-light shrink-0" />
                <a href={`mailto:${settings?.contact_email || "contact@douziemehomme-lejeu.com"}`} data-testid="footer-email" className="hover:text-rouge-light transition-colors break-all">
                  {settings?.contact_email || "contact@douziemehomme-lejeu.com"}
                </a></li>
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-rouge-light shrink-0" />
                <a href={`tel:${(settings?.phone || "").replace(/\s/g, "")}`} data-testid="footer-phone" className="hover:text-rouge-light transition-colors">{settings?.phone}</a></li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-rouge-light shrink-0" /><span>{settings?.address}</span></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-cream/10">
          <div className="container-x py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-cream/50">
            <p>© {new Date().getFullYear()} DOUZIEME HOMME LE JEU — SAS · SIRET {settings?.siret || "991 190 760"}</p>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
              <Link to="/mentions-legales" data-testid="footer-link-mentions-legales" className="hover:text-rouge-light transition-colors">Mentions légales</Link>
              <Link to="/cgv" data-testid="footer-link-cgv" className="hover:text-rouge-light transition-colors">CGV</Link>
              <Link to="/politique-confidentialite" data-testid="footer-link-confidentialite" className="hover:text-rouge-light transition-colors">Confidentialité</Link>
              <Link to="/politique-cookies" data-testid="footer-link-cookies" className="hover:text-rouge-light transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
